<template>
  <div class="generate-response-schema">
    <a-page-header title="生成响应格式" />
    
    <a-card :bordered="false" style="margin-top: 16px">
      <!-- 输入描述区域 -->
      <template #title>
        <span>输入描述</span>
      </template>
      
      <a-textarea
        v-model:value="description"
        :placeholder="placeholderText"
        :rows="8"
        :maxlength="2000"
        show-count
        @input="handleDescriptionInput"
      />
      
      <div style="margin-top: 16px; display: flex; justify-content: space-between; align-items: center">
        <div>
          <a-button type="link" @click="showExamples">查看示例</a-button>
        </div>
        <div>
          <a-select
            v-model:value="selectedModelId"
            placeholder="请选择模型"
            style="width: 300px; margin-right: 8px"
            :loading="modelsLoading"
            :dropdown-match-select-width="false"
            :dropdown-style="{ minWidth: '400px' }"
          >
            <a-select-option
              v-for="model in models"
              :key="model.id"
              :value="model.id"
            >
              {{ model.name }} ({{ model.source }})
            </a-select-option>
          </a-select>
          <a-button
            type="primary"
            :loading="generating"
            :disabled="!canGenerate"
            @click="handleGenerate"
          >
            生成
          </a-button>
          <a-button style="margin-left: 8px" @click="handleClear">清空</a-button>
        </div>
      </div>
    </a-card>

    <!-- 生成结果区域 -->
    <a-card
      v-if="result || errorMessage"
      :bordered="false"
      style="margin-top: 16px"
    >
      <template #title>
        <span>生成结果</span>
      </template>

      <!-- 错误信息 -->
      <a-alert
        v-if="errorMessage"
        :message="errorMessage"
        type="error"
        show-icon
        closable
        @close="errorMessage = ''"
        style="margin-bottom: 16px"
      />

      <!-- 警告信息 -->
      <a-alert
        v-if="warnings && warnings.length > 0"
        :message="'警告信息'"
        type="warning"
        show-icon
        style="margin-bottom: 16px"
      >
        <template #description>
          <ul style="margin: 0; padding-left: 20px">
            <li v-for="(warning, index) in warnings" :key="index">{{ warning }}</li>
          </ul>
        </template>
      </a-alert>

      <!-- 结果展示 -->
      <div v-if="result">
        <CodeEditor
          :model-value="formattedResult"
          language="json"
          :height="400"
          :read-only="true"
        />
        
        <div style="margin-top: 16px">
          <a-button type="primary" @click="handleCopy">复制JSON</a-button>
        </div>
      </div>
    </a-card>

    <!-- 提示信息 -->
    <a-card :bordered="false" style="margin-top: 16px">
      <a-typography-paragraph>
        <ul style="margin: 0; padding-left: 20px">
          <li>从模型配置中选择一个启用的模型</li>
          <li>字段名统一使用camelCase命名</li>
          <li>输出格式符合outputParams扩展格式规范</li>
        </ul>
      </a-typography-paragraph>
    </a-card>

    <!-- 示例弹窗 -->
    <a-modal
      v-model:visible="examplesVisible"
      title="示例"
      width="800px"
      :footer="null"
    >
      <a-list :data-source="examples" bordered>
        <template #renderItem="{ item }">
          <a-list-item>
            <a-list-item-meta>
              <template #title>
                <a @click="useExample(item)">{{ item.title }}</a>
              </template>
              <template #description>
                <div style="margin-top: 8px">
                  <a-typography-paragraph strong>输入：</a-typography-paragraph>
                  <pre style="background: #f5f5f5; padding: 8px; border-radius: 4px; white-space: pre-wrap">{{ item.input }}</pre>
                  <a-typography-paragraph strong style="margin-top: 12px">输出：</a-typography-paragraph>
                  <pre style="background: #f5f5f5; padding: 8px; border-radius: 4px; white-space: pre-wrap">{{ item.output }}</pre>
                </div>
              </template>
            </a-list-item-meta>
          </a-list-item>
        </template>
      </a-list>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import CodeEditor from '@/components/CodeEditor.vue';
import { getModels, generateResponseSchema } from '@/api/promptSimulator';

export default {
  name: 'GenerateResponseSchema',
  components: {
    CodeEditor
  },
  setup() {
    const description = ref('');
    const selectedModelId = ref(null);
    const models = ref([]);
    const modelsLoading = ref(false);
    const generating = ref(false);
    const result = ref(null);
    const formattedResult = ref('');
    const warnings = ref([]);
    const errorMessage = ref('');
    const examplesVisible = ref(false);

    const placeholderText = `请输入您期望的输出格式描述...

示例：
- diagnosis: 诊断结果，字符串，最多500字符
- confidence: 置信度，0到1之间的数字
- suggestions: 建议列表，字符串数组，1到10个元素`;

    const examples = [
      {
        title: '简单字段描述',
        input: `我需要一个输出格式，包含以下字段：
- diagnosis: 诊断结果，字符串类型，最多500个字符
- confidence: 置信度，0到1之间的数字
- timestamp: 时间戳，整数类型`,
        output: `{
  "diagnosis": {
    "type": "string",
    "description": "诊断结果",
    "maxLength": 500
  },
  "confidence": {
    "type": "number",
    "description": "置信度",
    "minimum": 0,
    "maximum": 1
  },
  "timestamp": {
    "type": "integer",
    "description": "时间戳"
  }
}`
      },
      {
        title: '带约束条件的字段',
        input: `输出格式要求：
1. result - 结果状态，只能是"success"、"failed"、"pending"中的一个
2. score - 评分，整数，范围是0到100
3. message - 消息内容，字符串，最少10个字符，最多200个字符`,
        output: `{
  "result": {
    "type": "string",
    "description": "结果状态",
    "enum": ["success", "failed", "pending"]
  },
  "score": {
    "type": "integer",
    "description": "评分",
    "minimum": 0,
    "maximum": 100
  },
  "message": {
    "type": "string",
    "description": "消息内容",
    "minLength": 10,
    "maxLength": 200
  }
}`
      },
      {
        title: '数组类型字段',
        input: `需要包含：
- items: 字符串数组，最少1个元素，最多20个元素
- tags: 标签列表，每个标签是字符串
- scores: 分数数组，每个分数是0到100之间的整数`,
        output: `{
  "items": {
    "type": "array",
    "description": "字符串数组",
    "items": {
      "type": "string"
    },
    "minItems": 1,
    "maxItems": 20
  },
  "tags": {
    "type": "array",
    "description": "标签列表",
    "items": {
      "type": "string"
    }
  },
  "scores": {
    "type": "array",
    "description": "分数数组",
    "items": {
      "type": "integer",
      "minimum": 0,
      "maximum": 100
    }
  }
}`
      },
      {
        title: '复杂嵌套结构',
        input: `输出格式：
- data: 对象类型，包含name（字符串）和value（数字）两个子字段
- metadata: 元数据对象，包含version（字符串）和count（整数）`,
        output: `{
  "data": {
    "type": "object",
    "description": "数据对象",
    "properties": {
      "name": {
        "type": "string",
        "description": "名称"
      },
      "value": {
        "type": "number",
        "description": "值"
      }
    }
  },
  "metadata": {
    "type": "object",
    "description": "元数据对象",
    "properties": {
      "version": {
        "type": "string",
        "description": "版本号"
      },
      "count": {
        "type": "integer",
        "description": "数量"
      }
    }
  }
}`
      }
    ];

    const canGenerate = computed(() => {
      return description.value.trim().length >= 10 && selectedModelId.value !== null && !generating.value;
    });

    // 加载模型列表
    const loadModels = async () => {
      modelsLoading.value = true;
      try {
        const response = await getModels();
        if (response.code === 200) {
          models.value = response.data || [];
          if (models.value.length > 0 && !selectedModelId.value) {
            selectedModelId.value = models.value[0].id;
          }
        } else {
          message.error(response.message || '获取模型列表失败');
        }
      } catch (error) {
        console.error('加载模型列表失败:', error);
        message.error('加载模型列表失败');
      } finally {
        modelsLoading.value = false;
      }
    };

    // 处理描述输入
    const handleDescriptionInput = () => {
      errorMessage.value = '';
      result.value = null;
    };

    // 生成Schema
    const handleGenerate = async () => {
      if (!canGenerate.value) {
        return;
      }

      generating.value = true;
      errorMessage.value = '';
      result.value = null;
      warnings.value = [];
      formattedResult.value = '';

      try {
        const response = await generateResponseSchema({
          description: description.value.trim(),
          model_id: selectedModelId.value
        });

        if (response.code === 200) {
          result.value = response.data.schema;
          formattedResult.value = response.data.formatted || JSON.stringify(response.data.schema, null, 2);
          warnings.value = response.data.warnings || [];
          message.success('生成成功');
        } else {
          errorMessage.value = response.message || '生成失败';
          if (response.data && response.data.errors) {
            errorMessage.value += ': ' + response.data.errors.join('; ');
          }
        }
      } catch (error) {
        console.error('生成失败:', error);
        errorMessage.value = error.message || '生成失败，请稍后重试';
      } finally {
        generating.value = false;
      }
    };

    // 清空
    const handleClear = () => {
      description.value = '';
      result.value = null;
      formattedResult.value = '';
      errorMessage.value = '';
      warnings.value = [];
    };

    // 复制JSON
    const handleCopy = async () => {
      if (!formattedResult.value) {
        message.warning('没有可复制的内容');
        return;
      }

      try {
        await navigator.clipboard.writeText(formattedResult.value);
        message.success('已复制到剪贴板');
      } catch (error) {
        console.error('复制失败:', error);
        message.error('复制失败');
      }
    };

    // 显示示例
    const showExamples = () => {
      examplesVisible.value = true;
    };

    // 使用示例
    const useExample = (example) => {
      description.value = example.input;
      examplesVisible.value = false;
      message.info('示例已填充到输入框');
    };

    onMounted(() => {
      loadModels();
    });

    return {
      description,
      selectedModelId,
      models,
      modelsLoading,
      generating,
      result,
      formattedResult,
      warnings,
      errorMessage,
      examplesVisible,
      placeholderText,
      examples,
      canGenerate,
      handleDescriptionInput,
      handleGenerate,
      handleClear,
      handleCopy,
      showExamples,
      useExample
    };
  }
};
</script>

<style scoped>
.generate-response-schema {
  padding: 24px;
}

pre {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
}
</style>

